// Thin API client for the admin team-rewards surfaces.
// Mirrors apps/api/src/api/team-rewards-admin.routes.ts.

import { api, ApiError } from '../../../lib/api';
import type { TeamReward, RewardContribution } from '../../coins/types';

export { ApiError };

export interface CreateRewardBody {
  name: string;
  description?: string;
  image_url?: string;
  coin_cost: number;
}

export interface UpdateRewardBody {
  name?: string;
  description?: string;
  image_url?: string | null;
  coin_cost?: number;
}

export async function listTeamRewards(
  teamId: string,
  includeArchived = false,
): Promise<TeamReward[]> {
  const url = `/api/admin/teams/${encodeURIComponent(teamId)}/rewards` +
    (includeArchived ? '?include_archived=true' : '');
  const res = await api<{ rewards: TeamReward[] }>(url);
  return res.rewards;
}

// Cross-team list for the top-level /admin/rewards page.
export async function listAllRewards(teamId?: string): Promise<TeamReward[]> {
  const url = '/api/admin/rewards' + (teamId ? `?team_id=${encodeURIComponent(teamId)}` : '');
  const res = await api<{ rewards: TeamReward[] }>(url);
  return res.rewards;
}

// Cross-team create: the operator picks the team in the form.
export async function createRewardForTeam(
  teamId: string,
  body: CreateRewardBody,
): Promise<TeamReward> {
  const res = await api<{ reward: TeamReward }>(
    '/api/admin/rewards',
    { method: 'POST', body: { ...body, team_id: teamId } },
  );
  return res.reward;
}

export async function getTeamReward(teamId: string, id: string): Promise<TeamReward> {
  const res = await api<{ reward: TeamReward }>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/rewards/${encodeURIComponent(id)}`,
  );
  return res.reward;
}

export async function createTeamReward(
  teamId: string,
  body: CreateRewardBody,
): Promise<TeamReward> {
  const res = await api<{ reward: TeamReward }>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/rewards`,
    { method: 'POST', body },
  );
  return res.reward;
}

export async function updateTeamReward(
  teamId: string,
  id: string,
  body: UpdateRewardBody,
): Promise<TeamReward> {
  const res = await api<{ reward: TeamReward }>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/rewards/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
  );
  return res.reward;
}

export async function claimTeamReward(teamId: string, id: string): Promise<TeamReward> {
  const res = await api<{ reward: TeamReward }>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/rewards/${encodeURIComponent(id)}/claim`,
    { method: 'POST' },
  );
  return res.reward;
}

export async function archiveTeamReward(teamId: string, id: string): Promise<void> {
  await api(
    `/api/admin/teams/${encodeURIComponent(teamId)}/rewards/${encodeURIComponent(id)}/archive`,
    { method: 'POST' },
  );
}

export async function listRewardContributions(
  teamId: string,
  id: string,
): Promise<RewardContribution[]> {
  const res = await api<{ contributions: RewardContribution[] }>(
    `/api/admin/teams/${encodeURIComponent(teamId)}/rewards/${encodeURIComponent(id)}/contributions`,
  );
  return res.contributions;
}

export async function voidRewardContribution(
  teamId: string,
  id: string,
  contributionId: string,
): Promise<void> {
  await api(
    `/api/admin/teams/${encodeURIComponent(teamId)}/rewards/${encodeURIComponent(id)}/contributions/${encodeURIComponent(contributionId)}`,
    { method: 'DELETE' },
  );
}
